// @ts-strict-ignore
import { Material, Mesh, MeshBasicMaterialParameters, MeshPhongMaterial, Texture, Vector2 } from 'three';
import { Observable, of, Subscriber } from 'rxjs';
import { MetaElementUsageVO, RGBColorValueVO, SimpleTextValueVO, TableTextItemVO, TableTextRowVO, TableTextValueVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { SpriteMaterial } from '../../material/sprite-material';
import { NumberUtils } from '@shared/util/number-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { LabelFormat } from '@project/components/label/label-editor/label-format';
import { Connection } from '../connection';
import { StringUtils } from '@shared/util/string-utils';
import { ConfiguratorParameters } from '../../configurator-parameters/configurator-parameters';

export class TextureUtils {
  public static readonly TEXT_SCALE_MAX: number = 1.83;
  public static readonly TEXT_SCALE_MIN: number = 1;
  public static readonly MAX_TEXTURE_SIDE: number = 2048;
  public static readonly LINE_THICKNESS_MULTIPLIER: number = 3;
  public static readonly MAX_TEXT_FLOW_FONT_SIZE: number = 720;

  private static readonly HORIZONTAL_ALIGNMENT_LEFT: string = 'left';
  private static readonly HORIZONTAL_ALIGNMENT_CENTER: string = 'center';
  private static readonly HORIZONTAL_ALIGNMENT_RIGHT: string = 'right';

  private static readonly MAX_ALLOWED_IMAGE_WIDTH: number = 2048;
  private static readonly MAX_ALLOWED_IMAGE_HEIGHT: number = 2048;

  private static readonly meshBasicMaterialProperties: string[] = ['alphaMap', 'alphaTest', 'aoMap', 'aoMapIntensity', 'blendDst',
    'blendEquation', 'blendEquationAlpha', 'blending', 'blendSrc', 'blendSrcAlpha', 'clipIntersection', 'clippingPlanes', 'clipShadows',
    'color', 'colorWrite', 'combine', 'depthFunc', 'depthTest', 'depthWrite', 'combine', 'depthFunc', 'depthTest', 'depthWrite', 'envMap',
    'fog', 'lights', 'map', 'morphTargets', 'name', 'opacity', 'overdraw', 'polygonOffset', 'polygonOffsetFactor', 'polygonOffsetUnits',
    'precision', 'premultipliedAlpha', 'reflectivity', 'refractionRatio', 'shading', 'side', 'skinning', 'specularMap', 'transparent',
    'vertexColors', 'visible', 'wireframe', 'wireframeLinecap', 'wireframeLinejoin', 'wireframeLinewidth', 'blendDstAlpha'];
  private static readonly spriteMaterialParameters: string[] = [
    'name', 'side', 'opacity', 'transparent', 'blending', 'blendSrc', 'blendDst', 'blendEquation', 'blendSrcAlpha', 'blendDstAlpha',
    'blendEquationAlpha', 'depthFunc', 'depthTest', 'depthWrite', 'colorWrite', 'precision', 'polygonOffset', 'polygonOffsetFactor',
    'polygonOffsetUnits', 'alphaTest', 'premultipliedAlpha', 'overdraw', 'visible', 'fog', 'shading', 'vertexColors',
    'clippingPlanes', 'clipIntersection', 'clipShadows', 'color', 'opacity', 'map', 'aoMap', 'aoMapIntensity', 'specularMap', 'alphaMap',
    'envMap', 'combine', 'reflectivity', 'refractionRatio', 'shading', 'wireframe', 'wireframeLinewidth', 'wireframeLinecap',
    'wireframeLinejoin', 'skinning', 'morphTargets',
  ];

  public static createTexture(mesh: Mesh, textValue: SimpleTextValueVO = null, transparent: boolean = true, fillColor: number = 0xffffff,
    defaultRotation: boolean = true, fontSize: number = 40): void {
    if (textValue) {
      const material: SpriteMaterial = this.createTextMaterial(mesh, transparent, fillColor, defaultRotation);
      this.updateTexture(mesh, material, textValue, fillColor, fontSize, transparent);
    }
  }

  public static createTextMaterial(mesh: Mesh, transparent: boolean = true, fillColor: number = 0xffffff,
    defaultRotation: boolean = true, bitmapRotation: number = Math.PI): SpriteMaterial {
    const meshBasicMaterialParameters: MeshBasicMaterialParameters = this.getMaterialParameters(mesh.material as Material,
      this.spriteMaterialParameters);
    meshBasicMaterialParameters.transparent = true;
    meshBasicMaterialParameters.visible = true;
    const movieMaterial: SpriteMaterial = new SpriteMaterial(meshBasicMaterialParameters, fillColor, transparent, defaultRotation,
      ConfiguratorParameters.maxRendererAnisotropy);
    const rectangle: Rectangle3D = MathUtils.calculateBoundingUVRectangle(mesh.geometry);
    movieMaterial.setSize(rectangle.width, rectangle.height);
    if (defaultRotation) {
      // we switch width and height, because we apply 90 degrees rotation
      movieMaterial.bitmapRotation = Math.PI / 2;
    } else {
      movieMaterial.bitmapRotation = bitmapRotation;
    }
    return movieMaterial;
  }

  private static getMeshSize(mesh: Mesh): Vector2 {
    const geometry = mesh.geometry;
    if (geometry) {
      const rect: Rectangle3D = MathUtils.calculateBoundingUVRectangle(geometry);
      return new Vector2(rect.width, rect.height);
    } else {
      return new Vector2(20, 20);
    }
  }

  private static getTextScale(meshSize: Vector2): number {
    const maxTextSide: number = Math.max(meshSize.y, meshSize.x);
    let textScale: number = TextureUtils.MAX_TEXTURE_SIDE / maxTextSide;
    textScale = textScale > TextureUtils.TEXT_SCALE_MAX ? TextureUtils.TEXT_SCALE_MAX :
      (textScale < TextureUtils.TEXT_SCALE_MIN ? TextureUtils.TEXT_SCALE_MIN : textScale);
    return textScale;
  }

  public static updateTexture(mesh: Mesh, material: SpriteMaterial, textValue: SimpleTextValueVO, fillColor: number,
    fontSize: number, transparent: boolean): void {
    if (!textValue) {
      return;
    }
    const meshSize: Vector2 = this.getMeshSize(mesh);
    const textScale: number = this.getTextScale(meshSize);

    let width: number = meshSize.y * textScale;
    let height: number = meshSize.x * textScale;

    // width and height must be less than 2048
    const maxComponentValue: number = Math.max(width, height);
    if (maxComponentValue > TextureUtils.MAX_TEXTURE_SIDE) {
      width = width * TextureUtils.MAX_TEXTURE_SIDE / maxComponentValue;
      height = height * TextureUtils.MAX_TEXTURE_SIDE / maxComponentValue;
    }
    // const fontSize: number = textValue.fontSize;
    const context: CanvasRenderingContext2D = this.createCanvasContext(width, height);
    // set background color to non-transparent texture
    if (!transparent) {
      context.fillStyle = '#' + fillColor.toString(16);
      context.fillRect(0, 0, width, height);
    }
    // context.font.fontsize(fontSize);
    const textColor: RGBColorValueVO = textValue.bgColor;
    context.fillStyle = textColor.toString();
    this.setText(context, textValue, width, height, fontSize, textScale);

    const texture: Texture = new Texture(context.canvas);
    texture.needsUpdate = true;
    material.map = texture;
    material.needsUpdate = true;
    mesh.material = material;
  }

  private static setText(context: CanvasRenderingContext2D, textValue: SimpleTextValueVO, width: number, height: number,
    fontSize: number, fontScale: number): void {
    const horizontalAlignment: string = this.getHorizontalAlignment(textValue);
    context.textAlign = horizontalAlignment as CanvasTextAlign;
    const verticalAlignment: string = this.getVerticalAlignment(textValue);
    context.textBaseline = verticalAlignment as CanvasTextBaseline;
    this.setFont(context, fontSize, fontScale);
    let textX: number;
    let textY: number;
    switch (horizontalAlignment) {
      case this.HORIZONTAL_ALIGNMENT_LEFT:
        textX = 0;
        break;
      case this.HORIZONTAL_ALIGNMENT_CENTER:
        textX = width / 2;
        break;
      case this.HORIZONTAL_ALIGNMENT_RIGHT:
        textX = width;
        break;
    }

    switch (verticalAlignment) {
      case LabelFormat.VALIGN_TOP:
        textY = 0;
        break;
      case LabelFormat.VALIGN_MIDDLE:
        textY = height / 2;
        break;
      case LabelFormat.VALIGN_BOTTOM:
        textY = height;
        break;
    }

    if (context.measureText(textValue.text).width > width) {
      context.textAlign = 'start';
      context.textBaseline = 'top';
      this.fillTextToCanvas(context, textValue.text, Math.round(fontSize * fontScale), width, height);
    } else {
      context.fillText(textValue.text, textX, textY);
    }
  }

  private static fillTextToCanvas(context: CanvasRenderingContext2D, text: string, lineHeight: number, width: number, height: number): void {
    const textToWrite: string = text.replace(/(\r\n|\n|\r)/gm, ' ').replace(/\s+/g, ' ');
    let words: string[] = textToWrite.split(' ');

    if (words.length > 1) {
      let checkedWords: string[] = [];
      for (const word of words) {
        if (context.measureText(word).width > width) {
          checkedWords = checkedWords.concat(this.splitTextByCanvasWidth(context, word, width));
        } else {
          checkedWords.push(word);
        }
      }
      words = checkedWords;
    } else {
      words = this.splitTextByCanvasWidth(context, textToWrite, width);
    }

    const lines: string[] = [];
    let line: string = '';

    for (const word of words) {
      const linePlus: string = line + word + ' ';
      if (context.measureText(linePlus).width > width) {
        lines.push(line);
        line = word + ' ';
      } else {
        line = linePlus;
      }
    }
    lines.push(line);

    let y: number = 0;
    if (lines.length * lineHeight < height) {
      y = (height - lines.length * lineHeight) / 2;
    }
    for (const line of lines) {
      context.fillText(line.trim(), 0, y);
      y += lineHeight;
    }
  }

  private static splitTextByCanvasWidth(context: CanvasRenderingContext2D, text: string, width: number): string[] {
    const lines: string[] = [];
    let fromIdx: number = 0;
    let str: string = '';
    for (let idx = 1; idx <= text.length; idx++) {
      str = text.substr(fromIdx, idx - fromIdx);
      if (context.measureText(str).width > width) {
        lines.push(text.substr(fromIdx, idx - fromIdx - 1));
        fromIdx = idx - 1;
      }
    }
    lines.push(str);
    return lines;
  }

  private static getHorizontalAlignment(textValue: SimpleTextValueVO): string {
    switch (textValue.hAlignment) {
      case -1:
        return this.HORIZONTAL_ALIGNMENT_LEFT;
      case 0:
        return this.HORIZONTAL_ALIGNMENT_CENTER;
      case 1:
        return this.HORIZONTAL_ALIGNMENT_RIGHT;
      default:
        return this.HORIZONTAL_ALIGNMENT_CENTER;
    }
  }

  private static getVerticalAlignment(textValue: SimpleTextValueVO): string {
    return this.getVerticalAlignByIndex(textValue.vAlignment);
  }

  /**
   * Returns index of alignment in texture area usage.
   */
  public static getIndexByVerticalAlign(alignment: string): number {
    switch (alignment) {
      case LabelFormat.VALIGN_TOP:
        return -1;
      case LabelFormat.VALIGN_BOTTOM:
        return 1;
      default:
        // middle alignment
        return 0;
    }
  }

  /**
   * Returns vertical alignment by alignment index in texture area usage.
   */
  public static getVerticalAlignByIndex(index: number): string {
    switch (index) {
      case -1:
        return LabelFormat.VALIGN_TOP;
      case 0:
        return LabelFormat.VALIGN_MIDDLE;
      case 1:
        return LabelFormat.VALIGN_BOTTOM;
      default:
        return LabelFormat.VALIGN_DEFAULT;
    }
  }

  private static setFont(context: CanvasRenderingContext2D, fontSize: number, fontScale: number): void {
    this.applyDefaultFontToContext(context, fontSize, fontScale);
  }

  public static createImage(imageData: string, imageType: string, textureName: string,
    createSrc: boolean = true): Observable<HTMLImageElement> {
    return new Observable<HTMLImageElement>(
      ((subscriber: Subscriber<HTMLImageElement>) => {
        try {
          const image: HTMLImageElement = new Image();
          image.onload = (event: Event) => {
            const targetImage: HTMLImageElement = event.target as HTMLImageElement;
            subscriber.next(targetImage);
            subscriber.complete();
          };
          image.onerror = (error: any) => {
            subscriber.error(error);
          };
          image.src = createSrc ? ('data:image/' + imageType + ';base64,' + imageData) : imageData;
        } catch (error) {
          subscriber.error(error);
        }
      })
    );
  }

  public static createMeshPhongMaterial(material: Material): MeshPhongMaterial {
    if (!material) {
      return null;
    }
    const meshBasicMaterialParameters: MeshBasicMaterialParameters = this.getBasicMaterialParametersFromMaterial(material);
    const meshPhongMaterial: MeshPhongMaterial = new MeshPhongMaterial(meshBasicMaterialParameters);
    return meshPhongMaterial;
  }

  public static getBasicMaterialParametersFromMaterial(material: Material): MeshBasicMaterialParameters {
    if (!material) {
      return null;
    }
    return this.getMaterialParameters(material, this.meshBasicMaterialProperties);
  }

  private static getMaterialParameters(material: Material, parameters: string[]): MeshBasicMaterialParameters {
    if (!material) {
      return null;
    }
    const meshBasicMaterialParameters: MeshBasicMaterialParameters = {};
    parameters.forEach((property: string) => {
      if (material.hasOwnProperty(property)) {
        meshBasicMaterialParameters[property] = material[property];
      }
    });
    return meshBasicMaterialParameters;
  }

  public static mergeImages(imageA: HTMLImageElement, imageB: HTMLImageElement, useFirstImageSize: boolean): Observable<HTMLImageElement> {
    if (!imageA || !imageB) {
      return of(null);
    }
    const targetWidth: number = useFirstImageSize ? imageA.width : imageB.width;
    const targetHeight: number = useFirstImageSize ? imageA.height : imageB.height;
    if (NumberUtils.isNotNumber(targetWidth) || NumberUtils.isNotNumber(targetHeight)) {
      return of(null);
    }
    const context: CanvasRenderingContext2D = this.createCanvasContext(targetWidth, targetHeight);
    context.drawImage(imageA, 0, 0, targetWidth, targetHeight);
    context.drawImage(imageB, 0, 0, targetWidth, targetHeight);
    return new Observable<HTMLImageElement>((subscriber: Subscriber<HTMLImageElement>) => {
      try {
        if (context.canvas['convertToBlob'] || !context.canvas['toDataURL']) {
          (context.canvas['convertToBlob'] || context.canvas.toBlob).call(context.canvas).then((blob: Blob) => {
            const image: HTMLImageElement = new Image();
            image.onload = (event: Event) => {
              const targetImage: HTMLImageElement = event.target as HTMLImageElement;
              subscriber.next(targetImage);
              subscriber.complete();
            };
            image.onerror = (error: any) => {
              subscriber.error(error);
            };
            image.src = URL.createObjectURL(blob);
          });
        } else {
          const image: HTMLImageElement = new Image();
          image.onload = (event: Event) => {
            const targetImage: HTMLImageElement = event.target as HTMLImageElement;
            subscriber.next(targetImage);
            subscriber.complete();
          };
          image.onerror = (error: any) => {
            subscriber.error(error);
          };
          image.src = context.canvas.toDataURL();
        }
      } catch (error) {
        subscriber.error(error);
      }
    });
  }

  private static createCanvasElement(tryToCreateOffscreenCanvas: boolean = true): any {
    let canvas: any = document.createElement('canvas');
    if (tryToCreateOffscreenCanvas && typeof canvas['transferControlToOffscreen'] === 'function') {
      // check if function exists if for supporting browsers (like Safari and IE11), which do not have function 'transferControlToOffscreen'
      canvas = canvas['transferControlToOffscreen']();
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/transferControlToOffscreen
      // calling this method is necessary for firefox version >= 105 as otherwise context for newly created canvases is not empty and also cannot be cleared using such methods:
      // context.clearRect(0, 0, canvasWidth, canvasHeight);
      // or
      // context.fillStyle = '#00000000';
      // context.fillRect(0, 0, canvasWidth, canvasHeight);
    }
    return canvas;
  }

  public static createCanvasContext(width?: number, height?: number, tryToCreateOffscreenCanvas: boolean = true): CanvasRenderingContext2D {
    const targetCanvas: HTMLCanvasElement = this.createCanvasElement(tryToCreateOffscreenCanvas);
    if (NumberUtils.isNumber(width)) {
      targetCanvas.width = width;
    }
    if (NumberUtils.isNumber(height)) {
      targetCanvas.height = height;
    }
    return targetCanvas.getContext('2d');
  }

  public static getDataURLFromCanvas(canvas: any): string {
    if (!canvas) {
      return '';
    } else if (canvas['toDataURL']) {
      return canvas.toDataURL();
    } else if (canvas['transferToImageBitmap']) {
      // method 'transferToImageBitmap' is only used to check if it is an offscreen canvas
      // such method is not called because of problems in correctly dsiplaying images for textures outside of 3d view (Bug 36347)
      const context: CanvasRenderingContext2D = this.createCanvasContext(canvas.width, canvas.height, false);
      context.drawImage(canvas, 0, 0);
      return context.canvas.toDataURL();
    } else {
      return '';
    }
  }

  public static applyDefaultFontToContext(context: CanvasRenderingContext2D, fontSize: number, fontScale: number): void {
    const fontFamily: string = ConfiguratorParameters.labeling.defaultLabelingFontName;
    const calculatedFontSize: number = Math.round(fontSize * fontScale);
    context.font = calculatedFontSize + 'px ' + fontFamily;
  }

  public static validateCustomImage(image: HTMLImageElement, alertHelper: AlertHelper): HTMLImageElement | undefined {
    if (!image || !(image instanceof HTMLImageElement)) {
      alertHelper.showError('imageLoader.error.title.image_not_loaded', 'imageLoader.error.message.image_not_loaded');
      return undefined;
    }
    if (TextureUtils.MAX_ALLOWED_IMAGE_WIDTH < image.width || TextureUtils.MAX_ALLOWED_IMAGE_HEIGHT < image.height) {
      alertHelper.showError('imageLoader.error.title.image_too_big', 'imageLoader.error.message.image_too_big');
      return undefined;
    }
    return image;
  }

  public static hasConnectionTextureText(connection: Connection): boolean {
    if (!connection.metaElementUsage) {
      return false;
    }
    const metaElementUsage: MetaElementUsageVO = connection.metaElementUsage;
    if (!(metaElementUsage instanceof TextureAreaUsageVO)) {
      return false;
    }
    const textValue: TextValueVO = metaElementUsage.textValue;
    if (textValue instanceof SimpleTextValueVO) {
      const text: string = StringUtils.convertFromFlexHtmlToString(textValue.text);
      return !StringUtils.isEmpty(text);
    } else if (textValue instanceof TableTextValueVO) {
      const simpleTextValues: SimpleTextValueVO[] = this.getSimpleTextValues(textValue);
      for (const simpleTextValue of simpleTextValues) {
        const text: string = StringUtils.convertFromFlexHtmlToString(simpleTextValue.text);
        if (!StringUtils.isEmpty(text)) {
          return true;
        }
      }
    }
    return false;
  }

  public static getSimpleTextValues(tableTextValue: TableTextValueVO): SimpleTextValueVO[] {
    const simpleTextValues: SimpleTextValueVO[] = [];
    tableTextValue.rows.forEach((tableTextRow: TableTextRowVO) => {
      tableTextRow.items.forEach((tableTextItem: TableTextItemVO) => {
        const textValueVO: TextValueVO = tableTextItem.value;
        if (textValueVO && (textValueVO instanceof SimpleTextValueVO)) {
          simpleTextValues.push(textValueVO);
        }
      });
    });
    return simpleTextValues;
  }


}
